<script setup lang="ts">
import { onMounted } from 'vue'

interface Props {
  callToAction: () => void
  login: () => void
}

const props = defineProps<Props>()

onMounted(onLoad)

function onLoad() {
  // Scroll Reveal Animation
  const revealElements = document.querySelectorAll('.reveal')

  const revealCallback = (entries: any, observer: any) => {
    entries.forEach((entry: any) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active')
        observer.unobserve(entry.target)
      }
    })
  }

  const revealOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
  }

  const revealObserver = new IntersectionObserver(revealCallback, revealOptions)

  revealElements.forEach((el) => {
    revealObserver.observe(el)
  })

  // Smooth scrolling for anchor links
  document.querySelectorAll('.nav-links a[href^="#"], .hero-actions a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault()
      // eslint-disable-next-line
      // @ts-ignore
      const targetId = this.getAttribute('href').substring(1)
      const targetElement = document.getElementById(targetId)
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    })
  })
}
</script>

<template>
  <div class="microbrains-landing">
    <header class="hero">
      <div style="position: absolute; top: 2rem; left: 2rem; z-index: 100">
        <img src="/microbrains/logo.png" alt="Microbrains Logo" style="height: 56px; width: auto; max-width: 50vw" />
      </div>
      <div class="top-nav-right" style="position: absolute; top: 2.2rem; right: 2rem; z-index: 100">
        <a
          href="#"
          class="btn"
          style="
            background: transparent;
            color: var(--coac-dark-blue);
            font-weight: bold;
            font-size: 1.1rem;
            border: 2px solid var(--coac-dark-blue);
            padding: 0.5rem 1.5rem;
            border-radius: 8px;
          "
          @click="props.login()"
          >Log in</a
        >
      </div>
      <div class="hero-bg-accent glow-1"></div>
      <div class="hero-bg-accent glow-2"></div>

      <div class="hero-content reveal">
        <h1 class="hero-title">
          Sovereign AI<br />
          <span class="text-gradient">Process Automation</span>
        </h1>
        <p class="hero-subtitle">
          Microbrains is the ultimate open-architected automation platform. Build automations based on documents, deploy
          AI agents, and extract structured data from complex documents—all in one visual canvas and integrated to your
          Apps.
        </p>
        <div class="hero-actions">
          <a href="#" class="btn btn-primary lg" @click="props.callToAction">Start for free</a>
        </div>

        <div class="hero-stats">
          <div class="stat">Utilized by <strong>6</strong> out of <strong>20</strong> DAX Companies</div>
          <div class="stat">Providing <strong>&gt;200</strong> integrations</div>
        </div>
      </div>

      <div class="hero-visual reveal highlight-delay">
        <div class="glass-card visual-card canvas">
          <div class="visual-header">
            <span class="dot red"></span>
            <span class="dot yellow"></span>
            <span class="dot green"></span>
            <span class="header-title">microbrains-agent-builder.flow</span>
          </div>
          <div class="visual-body canvas-body">
            <!-- Complex Node Structure mimicking n8n -->
            <div class="node-canvas">
              <div class="canvas-column">
                <div class="node-item saifty-node" style="border-left-color: #1cb5ba">
                  <span class="icon">📄</span> Document Extraction
                </div>
                <div class="node-item database" style="border-left-color: #94a3b8">
                  <span class="icon">💬</span> Communication
                </div>
              </div>
              <svg class="connection-line-svg multi" width="60" height="120">
                <path
                  d="M0,25 C30,25 30,60 60,60"
                  stroke="#1cb5ba"
                  stroke-width="2"
                  fill="none"
                  class="animated-path fast"
                ></path>
                <path
                  d="M0,95 C30,95 30,60 60,60"
                  stroke="#94a3b8"
                  stroke-width="2"
                  fill="none"
                  class="animated-path fast"
                ></path>
              </svg>
              <div class="node-item logic glow-border"><span class="icon">🧠</span> AI Agent</div>
              <svg class="connection-line-svg" width="50" height="20">
                <path
                  d="M0,10 L50,10"
                  stroke="#00A7E7"
                  stroke-width="2"
                  fill="none"
                  stroke-dasharray="4"
                  class="animated-path"
                ></path>
              </svg>
              <div class="node-item database"><span class="icon">⚙️</span> Process Integration</div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <section id="integrations" class="integrations-marquee reveal">
      <p class="marquee-title">CONNECTS SEAMLESSLY WITH YOUR TECH STACK</p>
      <div class="marquee-track">
        <!-- Block 1 -->
        <div class="marquee-content">
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/sap.svg"
            alt="SAP"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/salesforce.svg"
            alt="Salesforce"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/amazon.svg"
            alt="AWS"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/jira.svg"
            alt="Jira"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/slack.svg"
            alt="Slack"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/postgresql.svg"
            alt="PostgreSQL"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/dropbox.svg"
            alt="Dropbox"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/hubspot.svg"
            alt="HubSpot"
            class="integration-logo"
          />
        </div>
        <!-- Block 2 -->
        <div class="marquee-content" aria-hidden="true">
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/sap.svg"
            alt="SAP"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/salesforce.svg"
            alt="Salesforce"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/amazon.svg"
            alt="AWS"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/jira.svg"
            alt="Jira"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/slack.svg"
            alt="Slack"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/postgresql.svg"
            alt="PostgreSQL"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/dropbox.svg"
            alt="Dropbox"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/hubspot.svg"
            alt="HubSpot"
            class="integration-logo"
          />
        </div>
        <!-- Block 3 -->
        <div class="marquee-content" aria-hidden="true">
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/sap.svg"
            alt="SAP"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/salesforce.svg"
            alt="Salesforce"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/amazon.svg"
            alt="AWS"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/jira.svg"
            alt="Jira"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/slack.svg"
            alt="Slack"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/postgresql.svg"
            alt="PostgreSQL"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/dropbox.svg"
            alt="Dropbox"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/hubspot.svg"
            alt="HubSpot"
            class="integration-logo"
          />
        </div>
        <!-- Block 4 -->
        <div class="marquee-content" aria-hidden="true">
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/sap.svg"
            alt="SAP"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/salesforce.svg"
            alt="Salesforce"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/amazon.svg"
            alt="AWS"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/jira.svg"
            alt="Jira"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/slack.svg"
            alt="Slack"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/postgresql.svg"
            alt="PostgreSQL"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/dropbox.svg"
            alt="Dropbox"
            class="integration-logo"
          />
          <img
            src="https://cdn.jsdelivr.net/npm/simple-icons@14.0.0/icons/hubspot.svg"
            alt="HubSpot"
            class="integration-logo"
          />
        </div>
      </div>
    </section>

    <!-- Bento Grid Scale Layout -->
    <section id="features" class="bento-section">
      <div class="bento-header reveal">
        <h2>One platform. <br />Infinite possibilities.</h2>
        <p>From simple data routing to complex AI-driven industrial processing.</p>
      </div>

      <div class>
        <!-- Feature Box 1: Document Extraction -->
        <div
          class="bento-box full-width reveal layout-row"
          style="background: linear-gradient(180deg, var(--bs-body-bg) 0%, #ffffff 100%)"
        >
          <div class="bento-content">
            <h3>General Document Extraction</h3>
            <p style="font-size: 1.15rem; line-height: 1.6; margin-bottom: 2rem">
              Deploy specialized automatic extraction specifically for industry processes, documents like Technical Data
              Sheets, Delivery Notes, Purchase Orders, invoices, manuals, compliance certificates, safety data sheets,
              certificate of analysis and many more. Turn unstructured PDFs and image scans into structured, API-ready
              JSON data, securely linked to your ERP systems or other interfaces.
            </p>
            <ul
              style="
                list-style-type: none;
                padding: 0;
                font-size: 1.05rem;
                display: flex;
                flex-direction: column;
                gap: 1rem;
                color: var(--bs-info);
              "
            >
              <li><strong>✓</strong> Multilingual support</li>
              <li><strong>✓</strong> Easy setup of Agents</li>
              <li><strong>✓</strong> Deep ERP Synchronization</li>
            </ul>
          </div>
          <div class="bento-visual doc-scanner-visual" style="flex: 1.2">
            <div class="bento-scan-wrap">
              <div class="doc-scan bento-scan">
                <div class="scanner-line"></div>
                <div class="text-lines">
                  <div class="line w-long"></div>
                  <div class="line w-short hl"></div>
                  <div class="line w-long"></div>
                  <div class="line w-med"></div>
                  <div class="line w-long"></div>
                  <div class="line w-long hl"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Feature Box 2: EU Sovereignty -->
        <div class="bento-box full-width reveal highlight-delay layout-row-reverse" style="background: var(--bs-white)">
          <div class="bento-content">
            <h3>EU Sovereignty & Security</h3>
            <p style="font-size: 1.15rem; line-height: 1.6; margin-bottom: 2rem">
              Operate fully inside secure EU jurisdictions with strict data governance boundaries. Designed explicitly
              for organizations operating critical information or those who want to keep their data internally. Running
              with European infrastructure and operations.
            </p>
            <ul
              style="
                list-style-type: none;
                padding: 0;
                font-size: 1.05rem;
                display: flex;
                flex-direction: column;
                gap: 1rem;
                color: var(--bs-info);
              "
            >
              <li><strong>✓</strong> Infrastructure in Germany</li>
              <li><strong>✓</strong> No external AI providers involved</li>
              <li><strong>✓</strong> Public Cloud & On-Premise Target Options</li>
            </ul>
          </div>
          <div class="bento-visual" style="flex: 1.2">
            <div class="sovereignty-grid">
              <img src="/microbrains/landing/sovereign-ai.png" alt="EU Sovereignty Seal 1" />
              <img src="/microbrains/landing/de_hub.png" alt="DE Hub" />
              <img src="/microbrains/landing/gaia-x.png" alt="Gaia-X" />
              <img src="/microbrains/landing/u5-ht.png" alt="U5-HT" />
            </div>
          </div>
        </div>

        <!-- Feature Box 3: Industry Processes -->
        <div
          class="bento-box full-width reveal layout-row"
          style="padding: 0; overflow: hidden; background: var(--bs-info)"
        >
          <div class="bento-content" style="padding: 6rem 4rem; z-index: 2; position: relative">
            <h3 style="color: #fff">Deep Systems Integration</h3>
            <p style="color: var(--bs-secondary-600); font-size: 1.15rem; margin-bottom: 2rem">
              Connect your proprietary business systems directly to your logic. Exchange data with our REST APIs - we
              seamlessly link specialized, industry-specific applications to modern enterprise cloud platforms without
              fragile custom middle-ware.
            </p>
            <ul
              style="
                list-style-type: none;
                padding: 0;
                font-size: 1.05rem;
                display: flex;
                flex-direction: column;
                gap: 1rem;
                color: var(--bs-white);
              "
            >
              <li><strong>✓</strong> Open API specifications</li>
              <li><strong>✓</strong> Reliable queueing for high transaction volumes</li>
              <li><strong>✓</strong> Real-time cross-system monitoring</li>
            </ul>
          </div>
          <div
            class="bento-visual full-image-visual"
            style="margin: 0; position: absolute; right: 0; width: 60%; height: 100%; z-index: 1"
          >
            <div
              style="
                background: linear-gradient(270deg, var(--bs-info), transparent);
                position: absolute;
                z-index: 2;
                width: 100%;
                height: 100%;
              "
            ></div>
            <img
              src="/microbrains/landing/industry_visual.png"
              style="width: 100%; height: 100%; object-fit: cover"
              alt="Industrial Tech"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- TRUE HIGH FIDELITY REPLICATIONS (RESTORED & BRANDED COAC) -->
    <section id="coac-premium" class="hfi-parent">
      <!-- BLOCK 1: Build AI Agents -->
      <div class="hfi-block ai-agents-block reveal">
        <div class="hfi-header-center">
          <h2>Keep track of AI</h2>
          <p>Don't build blind AI agents. See the full context of every decision.</p>
        </div>

        <div class="hfi-masonry-grid">
          <div class="hfi-card mc-large">
            <h3>Transparent Tracing</h3>
            <p>Track exactly what data your AI models are accessing. All sources are permanently visible.</p>
          </div>

          <div class="hfi-card mc-stacked">
            <div class="mc-top">
              <h3>Total Control</h3>
              <p>Keep your data securely on-prem.</p>
              <ul class="hfi-check-list">
                <li><span class="chk">✓</span> Local or Cloud Models</li>
                <li><span class="chk">✓</span> Absolute Data Privacy</li>
              </ul>
            </div>
          </div>

          <div class="hfi-card mc-tall">
            <h3>Human-in-the-Loop</h3>
            <p>
              Combine rigid rule-based logic with flexible AI. Stop executions and wait for human approval before
              proceeding.
            </p>

            <div class="hfi-chat-mock">
              <div class="chat-bubble left border-glow">Can you please verify the information? I am not sure.</div>
              <div class="chat-bubble right">Yes, thank you!</div>
            </div>
          </div>
        </div>
      </div>

      <!-- BLOCK 2: Code when you need it -->
      <div class="hfi-block code-side-block reveal">
        <div class="hfi-split">
          <div class="hfi-text-content">
            <h2>Code when you need it</h2>
            <p class="subtext">
              Don't get stuck with purely visual builders. Write code directly inside the workflow when you hit complex
              challenges.
            </p>

            <ul class="hfi-chev-list">
              <li>
                <span class="chev">&lt;/&gt;</span>
                <div class="chev-text"><strong>Write Python or JavaScript</strong> natively in any node.</div>
              </li>
              <li>
                <span class="chev">&lt;/&gt;</span>
                <div class="chev-text"><strong>Process complex objects</strong> without clicking a hundred times.</div>
              </li>
              <li>
                <span class="chev">&lt;/&gt;</span>
                <div class="chev-text"><strong>Run your real code</strong> safely against live data.</div>
              </li>
            </ul>
          </div>

          <div class="hfi-visual-panel">
            <div class="coac-ide-mock">
              <div class="ide-tab-bar">
                <div class="dots">
                  <span class="dot red"></span><span class="dot yel"></span><span class="dot grn"></span>
                </div>
                <div class="tabbed"><span class="active">JS Node</span><span>Python Node</span></div>
              </div>
              <div class="ide-code">
                <pre><code><span style="color:var(--coac-cyan);">return</span> items.<span style="color:#d8b4fe;">map</span>(item => ({
  json: { ...item.json, <span style="color:#22c55e;">evaluated: true</span> }
}));</code></pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- BLOCK 3: Move Fast -->
      <div class="hfi-block iteration-block reveal">
        <div class="hfi-bg-circuit"></div>
        <div class="hfi-split">
          <div class="hfi-text-content z2">
            <h2>Test with real data</h2>
            <p class="subtext">
              Stop guessing if your workflow is correct. Build workflows interactively by testing single nodes on the
              fly.
            </p>

            <div class="hfi-grid-list">
              <div class="hfi-gl-item">
                <span class="check">✓</span><span><strong>Run single steps</strong> to verify logic</span>
              </div>
              <div class="hfi-gl-item">
                <span class="check">✓</span><span><strong>Pin payload data</strong> for testing</span>
              </div>
              <div class="hfi-gl-item">
                <span class="check">✓</span><span><strong>Debug easily</strong> visually</span>
              </div>
              <div class="hfi-gl-item">
                <span class="check">✓</span><span><strong>Reliable deployments</strong> in seconds</span>
              </div>
            </div>
          </div>

          <div class="hfi-visual-panel z2">
            <div class="hfi-thunder-orb">
              <div class="orb-ring r-1"></div>
              <div class="orb-ring r-2"></div>
              <div class="orb-ring r-3"></div>
              <div class="orb-center">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" />
                </svg>
              </div>
            </div>
          </div>
          <div class="hero-actions">
            <a href="#" class="btn btn-primary lg" @click="props.callToAction">Start for free</a>
          </div>
        </div>
      </div>
    </section>

    <footer class="footer">
      <div
        class="footer-content"
        style="grid-template-columns: 1fr; justify-content: center; text-align: center; margin-bottom: 2rem"
      >
        <div class="footer-brand" style="align-items: center">
          <img src="/microbrains/landing/coac-logo.svg" alt="Coac" style="height: 40px; opacity: 0.8" />
        </div>
      </div>
      <div class="footer-bottom">
        <p>&copy; 2026 COAC GmbH. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<style scoped lang="css">
.microbrains-landing {
  /* Existing custom theme variables */
  --coac-cyan: #00a7e7;
  --coac-dark-blue: #032b43;
  --saifty-teal: #1cb5ba;

  /* Provided SAIFTY / User Variables */
  --bs-blue: #0d6efd;
  --bs-indigo: #6610f2;
  --bs-pink: #d63384;
  --bs-red: #dc3545;
  --bs-orange: #fd7e14;
  --bs-yellow: #ffc107;
  --bs-green: #198754;
  --bs-teal: #20c997;
  --bs-cyan: #0dcaf0;
  --bs-gray: #6c757d;
  --bs-gray-dark: #343a40;
  --bs-gray-100: #f8f9fa;
  --bs-gray-200: #e9ecef;
  --bs-gray-300: #dee2e6;
  --bs-gray-400: #ced4da;
  --bs-gray-500: #adb5bd;
  --bs-gray-600: #6c757d;
  --bs-gray-700: #495057;
  --bs-gray-800: #343a40;
  --bs-gray-900: #212529;
  --bs-primary: #4ea6e1;
  --bs-primary-800: #8099b7;
  --bs-secondary: #c1d4e7;
  --bs-secondary-800: #f4f6fb;
  --bs-secondary-600: #d9e0e9;
  --bs-success: #44b284;
  --bs-success-200: #44b28433;
  --bs-info: #04346f;
  --bs-warning: #ffb800;
  --bs-danger: #cb6363;
  --bs-danger-800: #f53232;
  --bs-white: #fff;
  --bs-black: #000;
  --bs-light: #ffffff80;
  --bs-dark: #2c435f;
  --bs-dark-800: #00336f60;
  --bs-purple: #673ab7;
  --bs-black-gray: #212121;
  --bs-light-gray: #e6eaee;

  --bs-font-sans-serif:
    'Blogger Sans', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  --bs-font-monospace: SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  --bs-body-font-family: var(--bs-font-sans-serif);
  --bs-body-font-size: 15px;
  --bs-body-bg: #f5f7fa;
  --bs-body-color: #212529;

  --bg-base: var(--bs-body-bg);
  --bg-gradient: radial-gradient(circle at top right, var(--bs-secondary-800), var(--bs-body-bg));
  --surface-main: #ffffff;
  --surface-alt: #f8fafc;
  --surface-border: var(--bs-gray-300);
  --text-main: var(--bs-body-color);
  --text-muted: var(--bs-gray-600);
  --transition-smooth: 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* Force Blogger Sans on everything outside of code blocks */
body,
h1,
h2,
h3,
h4,
h5,
h6,
p,
a,
div,
span,
button,
input {
  font-family: 'Blogger Sans', var(--bs-font-sans-serif);
}

code,
pre {
  font-family: var(--bs-font-monospace) !important;
}

body {
  background: var(--bg-gradient);
  background-color: var(--bg-base);
  color: var(--text-main);
  font-family: var(--bs-body-font-family);
  font-size: var(--bs-body-font-size);
  line-height: 1.5;
  overflow-x: hidden;
  scroll-behavior: smooth;
}

h1,
h2,
h3,
h4,
.brand-name {
  font-family: var(--bs-body-font-family);
  color: var(--bs-info);
  font-weight: 700;
}

/* Typography */
.text-gradient {
  background: linear-gradient(135deg, var(--coac-cyan), var(--coac-dark-blue));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Navbar */
.navbar {
  position: fixed;
  top: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--surface-border);
  z-index: 1000;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.brand-icon {
  height: 32px;
}

.brand-name {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: var(--coac-dark-blue);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.nav-links a {
  color: var(--text-main);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.nav-links a:hover {
  color: var(--coac-cyan);
}

/* Buttons */
.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: var(--transition-smooth);
  border: 1px solid transparent;
  cursor: pointer;
}

.btn-primary {
  background: var(--coac-cyan);
  color: #fff;
  box-shadow: 0 4px 14px rgba(0, 167, 231, 0.25);
}

.btn-primary:hover {
  background: #008fca;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 167, 231, 0.35);
}

.btn-secondary {
  background: #f1f5f9;
  border: 1px solid var(--surface-border);
  color: var(--coac-dark-blue);
}

.btn-secondary:hover {
  background: #e2e8f0;
  transform: translateY(-2px);
}

.btn.lg {
  padding: 1rem 2rem;
  font-size: 1.1rem;
}

/* Hero Section */
.hero {
  position: relative;
  max-width: 1300px;
  margin: 0 auto;
  padding: 10rem 2rem 6rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  min-height: 90vh;
}

.hero-bg-accent {
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  filter: blur(120px);
  z-index: -1;
  opacity: 0.15;
  animation: drift 20s infinite alternate linear;
}

.glow-1 {
  background: var(--coac-cyan);
  top: 5%;
  left: -10%;
}

.glow-2 {
  background: #032b43;
  bottom: -10%;
  right: -10%;
  animation-delay: -10s;
}

@keyframes drift {
  0% {
    transform: translate(0, 0) scale(1);
  }
  100% {
    transform: translate(50px, 50px) scale(1.1);
  }
}

.badge {
  display: inline-block;
  padding: 0.4rem 1rem;
  background: rgba(0, 167, 231, 0.1);
  border: 1px solid rgba(0, 167, 231, 0.3);
  color: var(--coac-cyan);
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}
.badge.blue {
  background: rgba(3, 43, 67, 0.1);
  border-color: rgba(3, 43, 67, 0.2);
  color: var(--coac-dark-blue);
  margin-bottom: 0.5rem;
}

.hero-title {
  font-size: 4.5rem;
  line-height: 1.05;
  margin-bottom: 1.5rem;
  letter-spacing: -1px;
}

.hero-subtitle {
  font-size: 1.2rem;
  color: var(--text-muted);
  margin-bottom: 2.5rem;
  max-width: 500px;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
}

.hero-stats {
  display: flex;
  gap: 2rem;
  border-top: 1px solid var(--surface-border);
  padding-top: 2rem;
}
.stat {
  font-size: 1.1rem;
  color: var(--text-muted);
}
.stat strong {
  font-weight: 700;
  color: var(--coac-dark-blue);
}

/* Hero Canvas (n8n style) */
.glass-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid var(--surface-border);
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.canvas {
  width: 120%; /* pull out of bounds slightly for scale */
  transform: translateX(-5%);
}

.visual-header {
  background: #f8fafc;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 1px solid var(--surface-border);
}

.header-title {
  margin-left: auto;
  font-size: 0.8rem;
  color: var(--text-muted);
  font-family: monospace;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}
.dot.red {
  background: #ef4444;
}
.dot.yellow {
  background: #eab308;
}
.dot.green {
  background: #22c55e;
}

.canvas-body {
  background: radial-gradient(#e2e8f0 1px, transparent 1px);
  background-size: 20px 20px;
  background-color: var(--surface-main);
  padding: 4rem 2rem;
  min-height: 400px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.node-canvas {
  display: flex;
  align-items: center;
  gap: 0;
}

.canvas-column {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.node-item {
  background: #ffffff;
  border: 1px solid var(--surface-border);
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 2;
  transition: var(--transition-smooth);
  font-size: 0.9rem;
}

.node-item:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.node-item.webhook {
  border-left: 4px solid #8b5cf6;
}
.node-item.database {
  border-left: 4px solid #f59e0b;
}
.node-item.saifty-node {
  border-left: 4px solid var(--saifty-teal);
}
.node-item.logic {
  border-left: 4px solid var(--coac-cyan);
}

.glow-border {
  box-shadow:
    0 0 0 2px rgba(0, 167, 231, 0.2),
    0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.connection-line-svg {
  position: relative;
  z-index: 1;
}

.animated-path {
  stroke-dashoffset: 100;
  animation: dash 2s linear infinite;
}
.animated-path.fast {
  animation-duration: 1.5s;
}

@keyframes dash {
  to {
    stroke-dashoffset: 0;
  }
}

/* Integrations Marquee */
.integrations-marquee {
  padding: 4rem 0;
  background: #f8fafc;
  border-top: 1px solid var(--surface-border);
  border-bottom: 1px solid var(--surface-border);
  overflow: hidden;
}

.marquee-title {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--text-muted);
  margin-bottom: 2rem;
}

.marquee-track {
  display: flex;
  width: max-content;
  animation: marquee 50s linear infinite;
}

.marquee-content {
  display: flex;
  gap: 4rem;
  padding: 0 2rem;
  align-items: center;
}

.integration-logo {
  height: 48px;
  opacity: 0.7;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05));
  transition: var(--transition-smooth);
}

.integration-logo:hover {
  opacity: 1;
  transform: scale(1.1);
}

@keyframes marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* Bento Grid */
.bento-section {
  max-width: 1200px;
  margin: 8rem auto;
  padding: 0 2rem;
}

.bento-header {
  text-align: center;
  margin-bottom: 4rem;
}
.bento-header h2 {
  font-size: 3.5rem;
  line-height: 1.1;
  margin-bottom: 1rem;
}
.bento-header p {
  font-size: 1.1rem;
  color: var(--text-muted);
}

.bento-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: auto;
  gap: 8rem;
}

.bento-box {
  background: #ffffff;
  border: 1px solid var(--surface-border);
  border-radius: 24px;
  padding: 5rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  transition: var(--transition-smooth);
  position: relative;
  overflow: hidden;
}

.bento-box.full-width {
  grid-column: span 2;
  background: linear-gradient(180deg, #ffffff 0%, #f0f7ff 100%);
  margin-bottom: 5rem;
  border-top: 4px solid var(--coac-cyan);
}

.bento-box.half-width {
  grid-column: span 1;
  min-height: 250px;
}

.layout-row {
  flex-direction: row;
  gap: 4rem;
  align-items: center;
}

.layout-row-reverse {
  flex-direction: row-reverse;
  gap: 4rem;
  align-items: center;
}

.layout-row .bento-content,
.layout-row-reverse .bento-content {
  flex: 1;
  max-width: 500px;
}

.layout-row .bento-visual,
.layout-row-reverse .bento-visual {
  flex: 1;
  margin-top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon-circle {
  width: 48px;
  height: 48px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
}

.bento-box:hover {
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.08);
  transform: translateY(-5px);
}

.bento-box h3 {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.bento-box.half-width h3 {
  font-size: 1.5rem;
}

.bento-box p {
  color: var(--text-muted);
  font-size: 1.05rem;
}

/* Code toggle mock */
.code-toggle-mock {
  background: #1e293b;
  border-radius: 12px;
  width: 100%;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  border: 1px solid #334155;
  overflow: hidden;
}

.tabs {
  display: flex;
  background: #0f172a;
  padding: 0 1rem;
  border-bottom: 1px solid #334155;
}
.tabs span {
  padding: 0.75rem 1.5rem;
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 500;
}
.tabs span.active {
  color: #fff;
  border-bottom: 2px solid var(--coac-cyan);
}
.code-body {
  padding: 1.5rem;
  color: #38bdf8;
  background: #1e293b;
  font-size: 0.9rem;
  overflow-x: auto;
}

.code-body pre {
  margin: 0;
  font-family: 'Consolas', monospace;
  white-space: pre-wrap;
}

/* Scanner inside Bento */
.bento-scan-wrap {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}
.bento-scan {
  width: 300px;
  height: 200px;
  background: #fff;
  border: 1px solid var(--surface-border);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 1.5rem;
}

.doc-scan {
  position: relative;
  overflow: hidden;
}

.text-lines {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.line {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
}

.w-long {
  width: 100%;
}
.w-med {
  width: 70%;
}
.w-short {
  width: 40%;
}
.hl {
  background: var(--saifty-teal);
}

.scanner-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: var(--saifty-teal);
  box-shadow: 0 0 10px 2px rgba(28, 181, 186, 0.5);
  animation: scan 3s infinite linear;
  z-index: 10;
}

@keyframes scan {
  0% {
    top: 0;
  }
  50% {
    top: 100%;
  }
  100% {
    top: 0;
  }
}

/* Image Box Modules */
/* Sovereignty Grid Styles */
.sovereignty-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4rem;
  width: 100%;
  place-items: center;
  background: var(--coac-dark-blue);
  padding: 3rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.sovereignty-grid img {
  width: 100%;
  max-width: 140px;
  height: auto;
  object-fit: contain;
  transition: transform 0.4s ease;
}

.sovereignty-grid img:hover {
  transform: scale(1.1);
}

/* ========================================================
   HIGH-FIDELITY REPLICATIONS (COAC BRANDED)
======================================================== */

.hfi-parent {
  background: var(--bs-body-bg); /* Match the rest of the site */
  padding: 8rem 0;
  font-family: 'Blogger Sans', sans-serif;
  color: var(--coac-dark-blue);
}

.hfi-block {
  max-width: 1400px;
  margin: 0 auto 10rem auto;
  position: relative;
  border-radius: 2rem;
  overflow: hidden;
  padding: 6rem;
  background: var(--coac-dark-blue);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.hfi-header-center {
  text-align: center;
  max-width: 800px;
  margin: 0 auto 5rem auto;
  position: relative;
  z-index: 2;
}

.hfi-header-center h2 {
  font-size: 4rem;
  line-height: 1.1;
  letter-spacing: -2px;
  color: #fff;
  margin-bottom: 2rem;
}

.hfi-header-center p {
  font-size: 1.35rem;
  color: var(--bs-gray-300);
}

/* AI Agents (Cyan Base) */
.ai-agents-block::before {
  content: '';
  position: absolute;
  top: -20%;
  left: 10%;
  right: 10%;
  height: 60%;
  background: radial-gradient(ellipse at center, rgba(0, 167, 231, 0.15) 0%, transparent 70%);
  box-shadow: 0 0 100px 50px rgba(0, 167, 231, 0.05);
  z-index: 0;
}

.hfi-masonry-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 2rem;
  position: relative;
  z-index: 2;
}

.hfi-card {
  background: #fff;
  border: 1px solid var(--bs-gray-200);
  border-top: 4px solid var(--coac-cyan);
  border-radius: 1.5rem;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.hfi-card h3 {
  color: var(--coac-dark-blue);
  font-size: 1.8rem;
  margin-bottom: 1.25rem;
  line-height: 1.2;
}

.hfi-card p {
  font-size: 1.05rem;
  color: var(--bs-gray-700);
  line-height: 1.6;
}

.mc-large {
  grid-column: span 2;
  grid-row: span 1;
}
.mc-stacked {
  grid-column: span 2;
  grid-row: span 1;
}
.mc-tall {
  grid-column: span 1;
  grid-row: span 2;
}

.hfi-btn-brand {
  background: var(--coac-cyan);
  color: #fff;
  border: none;
  padding: 0.85rem 2rem;
  font-weight: 600;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: 0.3s;
  margin-top: 1.5rem;
}
.hfi-btn-brand:hover {
  background: #008ebf;
}

.hfi-check-list {
  list-style: none;
  padding: 0;
  margin-top: 1.5rem;
}
.hfi-check-list li {
  margin-bottom: 0.75rem;
  color: var(--bs-gray-700);
  font-size: 1.05rem;
}
.hfi-check-list .chk {
  color: var(--coac-cyan);
  font-weight: bold;
  margin-right: 0.5rem;
}

.hfi-chat-mock {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.chat-bubble {
  padding: 1rem 1.5rem;
  border-radius: 1rem;
  font-size: 0.9rem;
  max-width: 85%;
  line-height: 1.4;
  color: var(--bs-gray-800);
}
.chat-bubble.right {
  background: var(--bs-gray-100);
  align-self: flex-end;
  border-bottom-right-radius: 0;
  border: 1px solid var(--bs-gray-200);
}
.chat-bubble.left {
  background: var(--coac-dark-blue);
  color: #fff;
  align-self: flex-start;
  border-bottom-left-radius: 0;
}
.border-glow {
  border: 1px solid rgba(0, 167, 231, 0.5);
  box-shadow: 0 0 15px rgba(0, 167, 231, 0.15);
}

/* Block 2: Code IDE */
.code-side-block {
  display: flex;
  overflow: hidden;
  padding: 0;
  border: none;
}
.hfi-split {
  display: flex;
  gap: 4rem;
  align-items: center;
  width: 100%;
  position: relative;
  z-index: 2;
  padding: 6rem;
}
.hfi-text-content {
  flex: 1;
}
.hfi-visual-panel {
  flex: 1.2;
  display: flex;
  justify-content: flex-end;
}

.hfi-text-content h2 {
  font-size: 3.5rem;
  line-height: 1.1;
  color: #fff;
  margin-bottom: 1.5rem;
  letter-spacing: -1px;
}
.hfi-text-content .subtext {
  font-size: 1.15rem;
  color: var(--bs-gray-300);
  margin-bottom: 3rem;
  line-height: 1.6;
}

.hfi-chev-list {
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}
.hfi-chev-list li {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}
.hfi-chev-list .chev {
  color: var(--coac-cyan);
  font-family: monospace;
  font-size: 1.5rem;
  font-weight: bold;
  margin-top: -0.2rem;
}
.hfi-chev-list .chev-text {
  color: #f4f6fb;
  font-size: 1.05rem;
}
.chev-text strong {
  color: #fff;
}

.coac-ide-mock {
  background: #0a0f1d;
  border-radius: 1rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.ide-tab-bar {
  background: #121828;
  padding: 1rem;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  gap: 2rem;
}
.dots {
  display: flex;
  gap: 6px;
}
.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}
.dot.red {
  background: #ef4444;
}
.dot.yel {
  background: #f59e0b;
}
.dot.grn {
  background: #22c55e;
}
.tabbed {
  display: flex;
  gap: 1rem;
  color: #64748b;
  font-size: 0.9rem;
}
.tabbed .active {
  color: var(--coac-cyan);
  border-bottom: 2px solid var(--coac-cyan);
}
.ide-code {
  padding: 2rem;
  font-family: 'Consolas', monospace;
  font-size: 1rem;
  color: #e2e8f0;
}

/* Block 3: Iteration Circuit */
.hfi-bg-circuit {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 75% 50%, rgba(4, 52, 111, 0.8) 0%, transparent 60%);
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.02) 0,
    rgba(255, 255, 255, 0.02) 1px,
    transparent 1px,
    transparent 40px
  );
}
.z2 {
  position: relative;
  z-index: 2;
}
.hfi-grid-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 3rem;
  color: #fff;
}
.hfi-gl-item {
  display: flex;
  gap: 1rem;
  font-size: 1.05rem;
}
.hfi-gl-item .check {
  color: #fff;
  font-weight: bold;
  background: var(--coac-cyan);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hfi-thunder-orb {
  position: relative;
  width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}
.orb-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(0, 167, 231, 0.4);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.r-1 {
  width: 140px;
  height: 140px;
  background: rgba(0, 167, 231, 0.1);
  animation: pulseOrb 2s infinite alternate;
}
.r-2 {
  width: 200px;
  height: 200px;
  border-width: 2px;
  border-color: rgba(255, 255, 255, 0.2);
}
.r-3 {
  width: 300px;
  height: 300px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  animation: spinDash 20s linear infinite;
}
.orb-center {
  width: 80px;
  height: 80px;
  background: var(--coac-cyan);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 0 50px rgba(0, 167, 231, 0.8),
    inset 0 0 20px rgba(255, 255, 255, 0.5);
  z-index: 10;
}
@keyframes pulseOrb {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.5;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 1;
  }
}
@keyframes spinDash {
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

/* Footer Updated */
.footer {
  border-top: 1px solid var(--surface-border);
  padding: 4rem 2rem 2rem;
  background: #f8fafc;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;
}

.footer-brand {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.footer-links {
  display: contents; /* Flattens into grid */
}

.link-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.link-group h4 {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.link-group a {
  color: var(--text-muted);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s;
}

.link-group a:hover {
  color: var(--coac-cyan);
}

.footer-bottom {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 2rem;
  border-top: 1px solid var(--surface-border);
  text-align: center;
  color: var(--text-muted);
  font-size: 0.8rem;
}

/* Animations */
.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

.highlight-delay {
  transition-delay: 0.2s;
}

/* Responsive */
@media (max-width: 1000px) {
  .hero {
    grid-template-columns: 1fr;
    padding-top: 8rem;
    text-align: center;
  }
  .hero-actions {
    justify-content: center;
  }
  .hero-stats {
    justify-content: center;
  }
  .canvas {
    width: 100%;
    transform: none;
  }
  .hero-title {
    font-size: 3.5rem;
  }
  .bento-grid {
    grid-template-columns: 1fr;
  }
  .bento-box.large {
    grid-column: span 1;
  }
  .footer-content {
    grid-template-columns: 1fr;
  }
  .nav-links {
    display: none;
  }
}
/* MOBILE RESPONSIVENESS */
@media (max-width: 900px) {
  html,
  body {
    overflow-x: hidden !important;
    width: 100% !important;
    position: relative;
    padding: 0;
    margin: 0;
  }
  header,
  section,
  footer,
  .hero,
  .bento-section,
  .hfi-parent,
  .integrations-marquee {
    overflow-x: hidden !important;
    max-width: 100vw !important;
    box-sizing: border-box !important;
  }
  .hero {
    display: flex !important;
    flex-direction: column !important;
    text-align: center !important;
    padding: 7rem 1.5rem 4rem !important;
    min-height: auto;
  }
  .hero-bg-accent {
    display: none !important;
  }
  .hero-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
  .hero-title {
    font-size: clamp(2rem, 8vw, 2.6rem);
    margin-bottom: 1rem;
    line-height: 1.1;
    text-align: center;
  }
  .hero-subtitle {
    font-size: 1rem;
    padding: 0 1rem;
    margin-bottom: 2rem;
    text-align: center;
  }
  .hero-stats {
    justify-content: center;
    text-align: center;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
  }
  .hero-stats .stat {
    font-size: 0.95rem;
  }
  .hero-stats strong {
    font-size: 1rem;
  }
  .hero-visual {
    display: none !important;
  }
  .glass-card.canvas {
    display: none !important;
  }
  .hero-actions {
    justify-content: center;
    margin-bottom: 2rem;
    width: 100%;
  }

  header.hero > div:first-child {
    top: 1rem !important;
    left: 1rem !important;
  }
  header.hero .top-nav-right {
    top: 1rem !important;
    right: 0.5rem !important;
  }
  header.hero > div:first-child > img {
    height: 35px !important;
  }
  .top-nav-right .btn {
    padding: 0.4rem 1rem !important;
    font-size: 0.95rem !important;
  }

  .canvas-body {
    min-height: 250px;
    padding: 2rem 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .node-canvas {
    transform: scale(0.65);
    transform-origin: center center;
  }

  .bento-section {
    padding: 4rem 1.5rem;
  }
  .bento-box.layout-row,
  .bento-box.layout-row-reverse {
    flex-direction: column !important;
    gap: 2rem;
  }
  .bento-content {
    max-width: 100% !important;
    padding: 2rem !important;
    font-size: 1rem;
  }
  .bento-visual {
    width: 100% !important;
    margin-left: 0 !important;
    right: 0 !important;
    position: relative !important;
  }
  .sovereignty-grid,
  .bento-grid {
    grid-template-columns: 1fr !important;
  }
  .bento-box.half-width {
    grid-column: span 1;
  }

  .hfi-parent,
  .hfi-block {
    padding: 3rem 1.5rem;
  }
  .hfi-split {
    flex-direction: column;
    padding: 1.5rem;
    gap: 2rem;
  }
  .hfi-masonry-grid {
    grid-template-columns: 1fr;
  }
  .mc-large,
  .mc-tall,
  .mc-stacked {
    grid-column: span 1;
    grid-row: span 1;
  }
  .hfi-text-content h2 {
    font-size: 2rem;
  }
  .hfi-glass-container,
  .coac-ide-mock,
  .hfi-thunder-orb {
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
  }
  .ide-code {
    padding: 1rem;
    font-size: 0.85rem;
    overflow-x: auto;
  }
  .orb-ring {
    width: 100%;
    max-width: 250px;
    height: auto;
    aspect-ratio: 1;
    margin: 0 auto;
  }

  .hfi-grid-list {
    grid-template-columns: 1fr;
  }
}
</style>
